'use client';

/**
 * Settings modal for support chat configuration and data management
 */

import React, { useState, useCallback } from 'react';
import type { SupportChatConfig, SupportChatUser } from '../types/support-chat';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: SupportChatConfig;
  user: SupportChatUser;
  onClearThreadHistory: () => Promise<boolean>;
  onClearUserInfo?: () => void;
}

export function SettingsModal({
  isOpen,
  onClose,
  config,
  user,
  onClearThreadHistory,
  onClearUserInfo,
}: SettingsModalProps) {
  const [isClearingThreads, setIsClearingThreads] = useState(false);
  const [isClearingUserInfo, setIsClearingUserInfo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isAuthenticated = user.isAuthenticated !== false && user.accessToken !== undefined && user.cognitoSub !== undefined;
  const hasUserInfo = !isAuthenticated && config.cookieStorage;

  const handleClearThreadHistory = useCallback(async () => {
    if (!confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
      return;
    }

    setIsClearingThreads(true);
    setError(null);
    setSuccess(null);

    try {
      const success = await onClearThreadHistory();
      if (success) {
        setSuccess('Chat history cleared successfully.');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError('Failed to clear chat history. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear chat history.');
    } finally {
      setIsClearingThreads(false);
    }
  }, [onClearThreadHistory, onClose]);

  const handleClearUserInfo = useCallback(() => {
    if (!confirm('Are you sure you want to clear your saved information? You will need to enter it again next time.')) {
      return;
    }

    setIsClearingUserInfo(true);
    setError(null);
    setSuccess(null);

    try {
      onClearUserInfo?.();
      setSuccess('User information cleared successfully.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear user information.');
    } finally {
      setIsClearingUserInfo(false);
    }
  }, [onClearUserInfo, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '20px',
              color: '#6b7280',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '24px',
          }}
        >
          {error && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#dc2626',
                }}
              >
                {error}
              </p>
            </div>
          )}

          {success && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#16a34a',
                }}
              >
                {success}
              </p>
            </div>
          )}

          {/* Data Management Section */}
          <div
            style={{
              marginBottom: '24px',
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Data Management
            </h3>

            {/* Clear Thread History */}
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  marginBottom: '8px',
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    marginBottom: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  Clear Chat History
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#6b7280',
                    lineHeight: '1.5',
                  }}
                >
                  Remove all stored support chat conversations from your account.
                </p>
              </div>
              <button
                onClick={handleClearThreadHistory}
                disabled={isClearingThreads}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isClearingThreads ? '#d1d5db' : '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: isClearingThreads ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                }}
              >
                {isClearingThreads ? 'Clearing...' : 'Clear History'}
              </button>
            </div>

            {/* Clear User Info (for anonymous users) */}
            {hasUserInfo && onClearUserInfo && (
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div
                  style={{
                    marginBottom: '8px',
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      marginBottom: '4px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    Clear Saved Information
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#6b7280',
                      lineHeight: '1.5',
                    }}
                  >
                    Remove your saved name and email. You will be asked to provide this information again next time you use the chat.
                  </p>
                </div>
                <button
                  onClick={handleClearUserInfo}
                  disabled={isClearingUserInfo}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isClearingUserInfo ? '#d1d5db' : '#f59e0b',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: isClearingUserInfo ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                >
                  {isClearingUserInfo ? 'Clearing...' : 'Clear Information'}
                </button>
              </div>
            )}
          </div>

          {/* Account Information */}
          <div>
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Account Information
            </h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    marginBottom: '4px',
                  }}
                >
                  Status
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  {isAuthenticated ? 'Authenticated User' : 'Guest User'}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    marginBottom: '4px',
                  }}
                >
                  Email
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#111827',
                    wordBreak: 'break-all',
                  }}
                >
                  {user.email}
                </div>
              </div>
              {user.name && (
                <div
                  style={{
                    marginTop: '12px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      marginBottom: '4px',
                    }}
                  >
                    Name
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    {user.name}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}





